// src/tabs/FinanciamentosSubTab.js
import React, { useState, useMemo } from "react";
import { Plus, Edit2, Trash2, Check, X, Landmark, TrendingDown, Coins, HelpCircle } from "lucide-react";
import { formatCentavos, parseCentavos } from "../utils/moneyUtils";

const centsToDecimalStr = (cents) => {
  if (cents === undefined || cents === null) return "";
  const abs = Math.abs(cents);
  const reais = Math.floor(abs / 100);
  const centavos = abs % 100;
  return `${reais},${String(centavos).padStart(2, "0")}`;
};

export default function FinanciamentosSubTab({ finance }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("create"); // create, edit
  const [editingId, setEditingId] = useState(null);

  // Form Fields
  const [name, setName] = useState("");
  const [valorPrincipal, setValorPrincipal] = useState("0,00");
  const [valorJuros, setValorJuros] = useState("0,00");
  const [tipo, setTipo] = useState("financiamento");
  const [dataAlvo, setDataAlvo] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [parcelaAtual, setParcelaAtual] = useState("1");
  const [parcelasTotal, setParcelasTotal] = useState("12");
  const [centroCusto, setCentroCusto] = useState("");

  const openCreateModal = () => {
    setModalType("create");
    setName("");
    setValorPrincipal("0,00");
    setValorJuros("0,00");
    setTipo("financiamento");
    setDataAlvo("");
    setObservacoes("");
    setParcelaAtual("1");
    setParcelasTotal("12");
    setCentroCusto(finance.centrosCusto[0]?.id || "");
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setModalType("edit");
    setEditingId(item.id);
    setName(item.nome || "");
    setValorPrincipal(centsToDecimalStr(item.valor_principal_centavos || 0));
    setValorJuros(centsToDecimalStr(item.valor_juros_centavos || 0));
    setTipo(item.tipo || "financiamento");
    setDataAlvo(item.data_alvo || "");
    setObservacoes(item.observacoes || "");
    setParcelaAtual(item.parcela_atual !== undefined ? String(item.parcela_atual) : "1");
    setParcelasTotal(item.parcelas_total !== undefined ? String(item.parcelas_total) : "12");
    setCentroCusto(item.centro_custo || "");
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    const principalCents = parseCentavos(valorPrincipal);
    const jurosCents = parseCentavos(valorJuros);

    const payload = {
      nome: name.trim().toUpperCase(),
      valor_principal_centavos: principalCents,
      valor_juros_centavos: jurosCents,
      saldo_devedor_centavos: principalCents + jurosCents,
      parcela_atual: parseInt(parcelaAtual, 10) || 0,
      parcelas_total: parseInt(parcelasTotal, 10) || 0,
      centro_custo: centroCusto || null,
      tipo,
      data_alvo: dataAlvo || null,
      observacoes: observacoes.trim() || null
    };

    try {
      if (modalType === "create") {
        await finance.createFinanciamento(payload);
      } else {
        await finance.updateFinanciamento(editingId, payload);
      }
      setIsModalOpen(false);
    } catch (err) {
      alert("Erro ao salvar contrato: " + err.message);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Tem certeza que deseja remover o contrato "${item.nome}"? Isso não gerará lançamentos de ajuste, apenas removerá a ficha do catálogo.`)) return;
    try {
      await finance.deleteFinanciamento(item.id, item.nome);
    } catch (err) {
      alert("Erro ao remover contrato: " + err.message);
    }
  };

  // Cálculos agregados
  const totals = useMemo(() => {
    let totalDebt = 0;
    let totalPrincipal = 0;
    let totalJuros = 0;
    let totalInvestments = 0;

    finance.financiamentos.forEach((f) => {
      const debt = f.saldo_devedor_centavos || 0;
      const princ = f.valor_principal_centavos || 0;
      const jur = f.valor_juros_centavos || 0;

      if (f.tipo === "investimento_programado") {
        totalInvestments += princ;
      } else {
        totalDebt += debt;
        totalPrincipal += princ;
        totalJuros += jur;
      }
    });

    return {
      activeCount: finance.financiamentos.filter(f => f.tipo !== "investimento_programado").length,
      investmentCount: finance.financiamentos.filter(f => f.tipo === "investimento_programado").length,
      totalDebt,
      totalPrincipal,
      totalJuros,
      totalInvestments
    };
  }, [finance.financiamentos]);

  return (
    <div className="flex-1 flex flex-col p-4 lg:p-6 overflow-hidden bg-slate-50">
      
      {/* ─── INDICADORES DE TOPO ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 shrink-0">
        
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 shadow-xs">
          <div className="h-10 w-10 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center shrink-0">
            <Landmark size={20} />
          </div>
          <div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Financiamentos</span>
            <h4 className="text-lg font-black text-slate-700">{totals.activeCount} contratos</h4>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 shadow-xs">
          <div className="h-10 w-10 bg-indigo-50 text-indigo-650 rounded-lg flex items-center justify-center shrink-0">
            <Coins size={20} />
          </div>
          <div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Principal + Juros</span>
            <div className="text-xs font-semibold text-slate-500 font-mono">
              P: {formatCentavos(totals.totalPrincipal)} / J: {formatCentavos(totals.totalJuros)}
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 shadow-xs">
          <div className="h-10 w-10 bg-rose-50 text-rose-600 rounded-lg flex items-center justify-center shrink-0">
            <TrendingDown size={20} />
          </div>
          <div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Total Devedor</span>
            <h4 className="text-base font-black text-rose-700 font-mono">{formatCentavos(totals.totalDebt)}</h4>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 shadow-xs">
          <div className="h-10 w-10 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center shrink-0">
            <Landmark size={20} />
          </div>
          <div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Investimentos Alvo</span>
            <h4 className="text-base font-black text-emerald-700 font-mono">{formatCentavos(totals.totalInvestments)}</h4>
          </div>
        </div>

      </div>

      {/* Header Tabela */}
      <div className="flex justify-between items-center gap-4 mb-4 shrink-0">
        <div>
          <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Aba do Sufoco (Fichas Contratuais)</h3>
          <p className="text-[11px] text-slate-400">Monitore juros, amortização do principal e investimentos planejados para o maquinário.</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow transition-colors"
        >
          <Plus size={14} /> Novo Contrato
        </button>
      </div>

      {/* Tabela */}
      <div className="flex-1 bg-white border border-slate-200 rounded-xl overflow-y-auto shadow-xs">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Tipo</th>
              <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Nome do Contrato</th>
              <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Centro de Custo</th>
              <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Parcelas</th>
              <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 font-mono text-right">Principal</th>
              <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 font-mono text-right">Juros</th>
              <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 font-mono text-right">Saldo Devedor</th>
              <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 text-right w-24">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs font-semibold text-slate-650">
            {finance.financiamentos.map((f) => {
              const progressPct = f.parcelas_total > 0 ? Math.min(100, Math.round((f.parcela_atual / f.parcelas_total) * 100)) : 0;
              const isInv = f.tipo === "investimento_programado";
              
              return (
                <tr key={f.id} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border ${
                      isInv 
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700" 
                        : "bg-blue-50 border-blue-200 text-blue-700"
                    }`}>
                      {isInv ? "Investimento" : "Financiamento"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-slate-800 font-bold">{f.nome}</span>
                      {f.observacoes && <span className="text-[10px] text-slate-400 font-normal italic">{f.observacoes}</span>}
                      {f.data_alvo && <span className="text-[9px] text-slate-500 font-bold uppercase">Meta: {f.data_alvo}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {f.expand?.centro_custo?.nome || "-"}
                  </td>
                  <td className="px-4 py-3">
                    {!isInv ? (
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-700">{f.parcela_atual} / {f.parcelas_total}</span>
                        <div className="w-12 bg-slate-100 rounded-full h-1 overflow-hidden">
                          <div className="bg-blue-650 h-1 rounded-full" style={{ width: `${progressPct}%` }} />
                        </div>
                        <span className="text-[9px] font-bold text-slate-400">{progressPct}%</span>
                      </div>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-600">
                    {formatCentavos(f.valor_principal_centavos || 0)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-500">
                    {formatCentavos(f.valor_juros_centavos || 0)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-slate-700">
                    {formatCentavos(f.saldo_devedor_centavos || 0)}
                  </td>
                  <td className="px-4 py-3 text-right flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => openEditModal(f)}
                      className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                      title="Editar"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(f)}
                      className="text-slate-400 hover:text-red-650 transition-colors p-1"
                      title="Remover"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {finance.financiamentos.length === 0 && (
              <tr>
                <td colSpan="8" className="px-4 py-10 text-center text-xs font-semibold text-slate-450">
                  Nenhum contrato de financiamento ou investimento programado registrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ─── MODAL DE CADASTRO / EDIÇÃO ────────────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 overflow-y-auto animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide">
                {modalType === "create" ? "Novo Contrato / Ficha" : "Editar Ficha Contratual"}
              </h4>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-5 flex-1 space-y-4">
              
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Nome do Contrato *</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  placeholder="EX: PRONAMPE ITAÚ 2026, BANCO BRASIL GIRO"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Tipo de Contrato *</label>
                  <select
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="financiamento">Financiamento / Empréstimo</option>
                    <option value="investimento_programado">Investimento Programado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Data Alvo (Opcional)</label>
                  <input
                    type="date"
                    value={dataAlvo}
                    onChange={(e) => setDataAlvo(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Valor Principal (R$) *</label>
                  <input
                    type="text"
                    required
                    value={valorPrincipal}
                    onChange={(e) => setValorPrincipal(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-bold font-mono text-right"
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Valor Juros (R$)</label>
                  <input
                    type="text"
                    required
                    value={valorJuros}
                    onChange={(e) => setValorJuros(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-bold font-mono text-right"
                    placeholder="0,00"
                  />
                </div>
              </div>

              {tipo !== "investimento_programado" && (
                <div className="grid grid-cols-2 gap-3 animate-fade-in">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Parcela Atual</label>
                    <input
                      type="number"
                      min="0"
                      required
                      value={parcelaAtual}
                      onChange={(e) => setParcelaAtual(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Total de Parcelas</label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={parcelasTotal}
                      onChange={(e) => setParcelasTotal(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Centro de Custo</label>
                <select
                  value={centroCusto}
                  onChange={(e) => setCentroCusto(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                >
                  <option value="">Nenhum...</option>
                  {finance.centrosCusto.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Observações Contratuais</label>
                <textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 h-16 resize-none font-semibold text-slate-700"
                  placeholder="Detalhamento adicional sobre carências, taxas de CDI, amortização, etc."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <Check size={16} /> Salvar Ficha
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
